import {
  Badge,
  Button,
  Group,
  Modal,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconCheck,
  IconLock,
  IconUser,
  IconUserCog,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { changePassword, updateProfile } from "@/api/auth";
import { useAccess } from "@/hooks/useAccess";
import { useAuthStore } from "@/stores/auth";
import { notify } from "@/lib/notify";
import { ROLE_LABELS } from "@/lib/permissions";
import { FormSection } from "@/components/FormSection";

// Kullanıcının kendi hesabı: ad soyad + şifre. Şube müdürü Kullanıcılar ekranını göremediği için
// kendi adını da yalnızca buradan düzeltebiliyor.
//
// İki ayrı form: her bölüm kendi başına kaydedilir, alttaki çubuk yalnızca modalı kapatır — tek bir
// "Kaydet" iki isteği birden atmak ve birinin hatasını diğerine karıştırmak zorunda kalırdı.
export function AccountModal({ opened, onClose }) {
  const user = useAuthStore((state) => state.user);
  const updateStoredUser = useAuthStore((state) => state.updateUser);
  const { isFull } = useAccess();
  const queryClient = useQueryClient();

  const profileForm = useForm({
    initialValues: { fullName: "" },
    validate: {
      fullName: (value) => (value.trim() ? null : "Ad soyad gerekli"),
    },
  });

  const passwordForm = useForm({
    initialValues: { currentPassword: "", newPassword: "", confirm: "" },
    validate: {
      currentPassword: (value) => (value ? null : "Mevcut şifre gerekli"),
      newPassword: (value) => (value.length >= 8 ? null : "En az 8 karakter"),
      confirm: (value, values) =>
        value === values.newPassword ? null : "Şifreler eşleşmiyor",
    },
  });

  // Her açılışta mevcut adla dolar ve şifre alanları temizlenir; kapatıp açan kullanıcı yarım
  // kalmış bir düzenlemeyle karşılaşmasın.
  useEffect(() => {
    if (opened) {
      profileForm.setValues({ fullName: user?.fullName ?? "" });
      passwordForm.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, user?.fullName]);

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      updateStoredUser({ fullName: data.fullName });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      notify.success("Ad soyad güncellendi");
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Ad soyad güncellenemedi"),
  });

  const passwordMutation = useMutation({
    mutationFn: (values) =>
      changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: () => {
      notify.success("Şifre değiştirildi");
      passwordForm.reset();
      onClose();
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Şifre değiştirilemedi"),
  });

  const trimmedName = profileForm.values.fullName.trim();
  // superadmin ROLE_LABELS'ta yok (tenant rolü değil); impersonation sırasında store 'owner'
  // taşıdığı için oradaki etiket doğru çıkıyor.
  const roleLabel =
    user?.role === "superadmin"
      ? "Süper Admin"
      : (ROLE_LABELS[user?.role] ?? (isFull ? ROLE_LABELS.owner : null));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      size="lg"
      padding="lg"
      title={
        <Group gap="sm">
          <ThemeIcon variant="light" color="chocolate" radius="md" size="lg">
            <IconUserCog size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>Hesap Ayarları</Text>
            <Text size="xs" c="dimmed">
              Kendi bilgilerini ve şifreni güncelle
            </Text>
          </div>
        </Group>
      }
    >
      <Stack gap="md">
        <FormSection
          icon={IconUser}
          title="Profil"
          description="Panelde ve kayıtlarda görünen adın"
          action={
            roleLabel ? (
              <Badge variant="light" color="chocolate" radius="sm">
                {roleLabel}
              </Badge>
            ) : null
          }
        >
          <form
            onSubmit={profileForm.onSubmit((values) =>
              profileMutation.mutate(values),
            )}
          >
            <Stack gap="sm">
              <TextInput
                label="Ad Soyad"
                placeholder="Ad Soyad"
                {...profileForm.getInputProps("fullName")}
              />
              <TextInput label="E-posta" value={user?.email ?? ""} disabled />
              <Group justify="center">
                <Button
                  type="submit"
                  variant="light"
                  color="chocolate"
                  leftSection={<IconCheck size={16} />}
                  loading={profileMutation.isPending}
                  disabled={!trimmedName || trimmedName === user?.fullName}
                >
                  Adı Kaydet
                </Button>
              </Group>
            </Stack>
          </form>
        </FormSection>

        <FormSection
          icon={IconLock}
          title="Şifre"
          description="En az 8 karakter"
        >
          <form
            onSubmit={passwordForm.onSubmit((values) =>
              passwordMutation.mutate(values),
            )}
          >
            <Stack gap="sm">
              <PasswordInput
                label="Mevcut Şifre"
                placeholder="••••••••"
                {...passwordForm.getInputProps("currentPassword")}
              />
              <PasswordInput
                label="Yeni Şifre"
                placeholder="••••••••"
                {...passwordForm.getInputProps("newPassword")}
              />
              <PasswordInput
                label="Yeni Şifre (tekrar)"
                placeholder="••••••••"
                {...passwordForm.getInputProps("confirm")}
              />
              <Group justify="center">
                <Button
                  type="submit"
                  variant="light"
                  color="chocolate"
                  leftSection={<IconLock size={16} />}
                  loading={passwordMutation.isPending}
                >
                  Şifreyi Değiştir
                </Button>
              </Group>
            </Stack>
          </form>
        </FormSection>
      </Stack>
    </Modal>
  );
}
